import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Descriptions,
  Timeline,
  Table,
  Tag,
  Typography,
  Spin,
  Alert,
  Divider,
  Space,
  message,
} from 'antd';
import {
  HistoryOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  CarOutlined,
  FileProtectOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { inventoryAPI } from '../services/api.ts';
import { documentTypeLabel } from '../utils/documentTypes.ts';

const { Text } = Typography;

interface ChainStage {
  stage: string;
  title: string;
  date: string | null;
  data: Record<string, any>;
}

interface StockMovement {
  id: number;
  movement_type: string;
  movement_date: string | null;
  quantity: number;
  source_type: string;
  reason?: string | null;
  from_udc_pallet?: string | null;
  from_udc_collo?: string | null;
  to_udc_pallet?: string | null;
  to_udc_collo?: string | null;
  performed_by_name?: string | null;
  warehouse_name?: string | null;
}

interface StockReservationRow {
  id: number;
  order_number?: string | null;
  customer_order_number?: string | null;
  customer_company?: string | null;
  quantity_reserved: number;
  source_type: string;
  status: string;
  reserved_at?: string | null;
}

interface StockHistoryData {
  stock: Record<string, any>;
  chain: ChainStage[];
  movements: StockMovement[];
  reservations: StockReservationRow[];
}

interface StockHistoryModalProps {
  visible: boolean;
  stockId: number | null;
  stockCode?: string;
  productDescription?: string;
  onClose: () => void;
}

const fmtDate = (v?: string | null): string => (v ? dayjs(v).format('DD/MM/YYYY') : '-');
const fmtDateTime = (v?: string | null): string => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-');

const getStageColor = (stage: string): string => {
  switch (stage) {
    case 'customer_order': return 'blue';
    case 'supplier_order': return 'geekblue';
    case 'shipment': return 'orange';
    case 'customs_im7': return 'gold';
    case 'customs_im4': return 'green';
    case 'customs_t1':
    case 'customs_ddt': return 'volcano';
    case 'stock': return 'cyan';
    default: return 'gray';
  }
};

const getStageIcon = (stage: string): React.ReactNode => {
  switch (stage) {
    case 'customer_order': return <ShoppingCartOutlined />;
    case 'supplier_order': return <ShopOutlined />;
    case 'shipment': return <CarOutlined />;
    case 'stock': return <InboxOutlined />;
    default: return <FileProtectOutlined />; // documenti doganali
  }
};

const getMovementColor = (type: string): string => {
  switch (type) {
    case 'IN': return 'green';
    case 'OUT': return 'red';
    case 'TRANSFER': return 'blue';
    case 'ADJUSTMENT': return 'orange';
    default: return 'default';
  }
};

const sourceTypeLabel = (st?: string): string => {
  switch (st) {
    case 'available_stock': return 'Disponibile';
    case 'in_production': return 'In produzione';
    case 'in_transit': return 'In transito';
    default: return st || '-';
  }
};

// Costruisce le coppie etichetta/valore mostrate per ciascuno stadio della catena
const stageFields = (st: ChainStage): Array<[string, React.ReactNode]> => {
  const d = st.data || {};
  let pairs: Array<[string, React.ReactNode] | null> = [];

  switch (st.stage) {
    case 'customer_order':
      pairs = [
        ['N° ordine', d.order_number],
        ['Rif. cliente', d.customer_order_number],
        ['Cliente', d.customer_company],
        ['Data ordine', fmtDate(d.order_date)],
        ['Consegna richiesta', fmtDate(d.requested_delivery_date)],
        d.status ? ['Stato', <Tag color="blue">{d.status}</Tag>] : null,
      ];
      break;
    case 'supplier_order':
      pairs = [
        ['N° ordine', d.order_number],
        ['Fornitore', d.supplier_name],
        ['Data ordine', fmtDate(d.order_date)],
        ['Consegna prevista', fmtDate(d.estimated_delivery_date || d.delivery_date)],
        d.status ? ['Stato', <Tag color="geekblue">{d.status}</Tag>] : null,
      ];
      break;
    case 'shipment':
      pairs = [
        ['Codice spedizione', d.shipment_code],
        d.shipment_type ? ['Tipo', <Tag color="orange">{d.shipment_type}</Tag>] : null,
        ['Fornitore', d.supplier_name],
        ['Targa mezzo', d.vehicle_plate],
        d.invoice_number ? ['Fattura fornitore', `${d.invoice_number}${d.invoice_date ? ' del ' + fmtDate(d.invoice_date) : ''}`] : null,
        d.packing_list_number ? ['Packing list', d.packing_list_number] : null,
        ['Partenza', fmtDate(d.actual_departure_date || d.departure_date)],
        ['Arrivo', fmtDate(d.actual_arrival_date || d.expected_arrival_date)],
        d.status ? ['Stato', <Tag>{d.status}</Tag>] : null,
      ];
      break;
    case 'stock':
      pairs = [
        ['Magazzino', d.warehouse_description],
        ['UDC', [d.udc_pallet, d.udc_collo].filter(Boolean).join(' / ') || '-'],
        ['Disponibile', d.qta_available],
        ['In produzione', d.qta_in_production],
        ['In transito', d.qta_in_transit],
      ];
      break;
    default: // customs_im7 / customs_im4 / customs_t1 / customs_ddt
      pairs = [
        d.document_type ? ['Tipo', <Tag color={getStageColor(st.stage)}>{documentTypeLabel(d.document_type)}</Tag>] : null,
        ['N° documento', d.document_number],
        ['Data documento', fmtDate(d.document_date)],
        ['Magazzino', d.warehouse_description],
        d.customs_status ? ['Stato dogana', <Tag>{d.customs_status}</Tag>] : null,
        d.is_manual_entry ? ['Inserimento', <Tag color="purple">MANUALE</Tag>] : null,
        d.original_quantity_loaded != null ? ['Q.tà caricata', d.original_quantity_loaded] : null,
        d.current_quantity_remaining != null ? ['Q.tà residua', d.current_quantity_remaining] : null,
      ];
      break;
  }

  return pairs.filter(
    (p): p is [string, React.ReactNode] =>
      p !== null && p[1] !== null && p[1] !== undefined && p[1] !== ''
  );
};

const StockHistoryModal: React.FC<StockHistoryModalProps> = ({
  visible,
  stockId,
  stockCode,
  productDescription,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StockHistoryData | null>(null);

  useEffect(() => {
    if (visible && stockId) {
      setLoading(true);
      setData(null);
      inventoryAPI
        .getStockHistory(stockId)
        .then((res: StockHistoryData) => setData(res))
        .catch((err: any) => {
          console.error('Errore caricamento storia stock:', err);
          message.error('Errore nel caricamento della storia del prodotto');
        })
        .finally(() => setLoading(false));
    }
  }, [visible, stockId]);

  const stock = data?.stock;

  const movementColumns = [
    {
      title: 'Data',
      dataIndex: 'movement_date',
      key: 'movement_date',
      width: 140,
      render: (v: string) => fmtDateTime(v),
    },
    {
      title: 'Tipo',
      dataIndex: 'movement_type',
      key: 'movement_type',
      width: 110,
      render: (v: string) => <Tag color={getMovementColor(v)}>{v}</Tag>,
    },
    {
      title: 'Q.tà',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right' as const,
      render: (v: number) => (v != null ? v.toLocaleString('it-IT') : '-'),
    },
    {
      title: 'Origine',
      dataIndex: 'source_type',
      key: 'source_type',
      width: 110,
      render: (v: string) => (v ? <Tag>{v}</Tag> : '-'),
    },
    {
      title: 'UDC (da → a)',
      key: 'udc',
      render: (_: any, r: StockMovement) => {
        const from = [r.from_udc_pallet, r.from_udc_collo].filter(Boolean).join('/');
        const to = [r.to_udc_pallet, r.to_udc_collo].filter(Boolean).join('/');
        if (!from && !to) return '-';
        return `${from || '-'} → ${to || '-'}`;
      },
    },
    {
      title: 'Utente',
      dataIndex: 'performed_by_name',
      key: 'performed_by_name',
      width: 120,
      render: (v: string) => v || '-',
    },
    {
      title: 'Causale',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (v: string) => v || '-',
    },
  ];

  const reservationColumns = [
    {
      title: 'Ordine cliente',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (v: string) => v || '-',
    },
    {
      title: 'Rif. cliente',
      dataIndex: 'customer_order_number',
      key: 'customer_order_number',
      render: (v: string) => v || '-',
    },
    {
      title: 'Cliente',
      dataIndex: 'customer_company',
      key: 'customer_company',
      render: (v: string) => v || '-',
    },
    {
      title: 'Q.tà',
      dataIndex: 'quantity_reserved',
      key: 'quantity_reserved',
      width: 90,
      align: 'right' as const,
      render: (v: number) => (v != null ? v.toLocaleString('it-IT') : '-'),
    },
    {
      title: 'Tipo sorgente',
      dataIndex: 'source_type',
      key: 'source_type',
      width: 130,
      render: (v: string) => <Tag>{sourceTypeLabel(v)}</Tag>,
    },
    {
      title: 'Prenotato il',
      dataIndex: 'reserved_at',
      key: 'reserved_at',
      width: 140,
      render: (v: string) => fmtDateTime(v),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      title={
        <Space>
          <HistoryOutlined />
          <span>Storia prodotto{stockCode ? ` — ${stockCode}` : ''}</span>
        </Space>
      }
      destroyOnClose
    >
      <Spin spinning={loading}>
        {/* Intestazione: stato attuale dello stock */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Prodotto" span={2}>
              <Text strong>{stock?.internal_product_code || '-'}</Text>
              {stock?.product_description ? ` — ${stock.product_description}` : (productDescription ? ` — ${productDescription}` : '')}
            </Descriptions.Item>
            {(stock?.size || stock?.color) && (
              <Descriptions.Item label="Taglia / Colore">
                {[stock?.size, stock?.color].filter(Boolean).join(' / ') || '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Magazzino">{stock?.warehouse_description || '-'}</Descriptions.Item>
            <Descriptions.Item label="UDC">
              {[stock?.udc_pallet, stock?.udc_collo].filter(Boolean).join(' / ') || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Cliente">{stock?.customer_company || '-'}</Descriptions.Item>
            <Descriptions.Item label="Quantità">
              <Space size="small" wrap>
                <Tag color="green">Disp.: {stock?.qta_available ?? 0}</Tag>
                <Tag color="blue">In prod.: {stock?.qta_in_production ?? 0}</Tag>
                <Tag color="orange">In transito: {stock?.qta_in_transit ?? 0}</Tag>
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Catena di provenienza */}
        <Divider orientation="left">Catena di provenienza</Divider>
        {data && data.chain && data.chain.length > 1 ? (
          <Timeline style={{ paddingLeft: 4 }}>
            {data.chain.map((st, idx) => (
              <Timeline.Item key={`${st.stage}-${idx}`} color={getStageColor(st.stage)}>
                <div>
                  <Space size="small">
                    {getStageIcon(st.stage)}
                    <Text strong>{st.title}</Text>
                    {st.date && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {fmtDate(st.date)}
                      </Text>
                    )}
                  </Space>
                  <div style={{ marginTop: 4 }}>
                    {stageFields(st).map(([label, value]) => (
                      <div key={label} style={{ fontSize: 13, lineHeight: '20px' }}>
                        <Text type="secondary">{label}:</Text> {value}
                      </div>
                    ))}
                  </div>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          !loading && (
            <Alert
              type="info"
              showIcon
              message="Nessuna informazione di provenienza disponibile per questo stock (probabilmente creato manualmente o senza collegamenti documentali)."
            />
          )
        )}

        {/* Movimenti di magazzino */}
        <Divider orientation="left">Movimenti di magazzino</Divider>
        {data && data.movements && data.movements.length > 0 ? (
          <Table
            size="small"
            rowKey="id"
            columns={movementColumns}
            dataSource={data.movements}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        ) : (
          !loading && (
            <Alert type="info" showIcon message="Nessun movimento di magazzino registrato per questo stock." />
          )
        )}

        {/* Prenotazioni correnti */}
        <Divider orientation="left">Prenotazioni correnti</Divider>
        {data && data.reservations && data.reservations.length > 0 ? (
          <Table
            size="small"
            rowKey="id"
            columns={reservationColumns}
            dataSource={data.reservations}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        ) : (
          !loading && (
            <Alert type="info" showIcon message="Nessuna prenotazione attiva su questo stock." />
          )
        )}
      </Spin>
    </Modal>
  );
};

export default StockHistoryModal;
